import { Link } from '@tanstack/react-router';
import { Download, ExternalLink, Loader2 } from 'lucide-react';

import type {
  Report,
  ReportCategory,
} from '~pages/payments/payment-reports/report-config';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';

const iconColors: Record<ReportCategory, string> = {
  Revenue: 'text-emerald-600 bg-emerald-50',
  Finance: 'text-blue-600 bg-blue-50',
  Analytics: 'text-purple-600 bg-purple-50',
  Enrollment: 'text-amber-600 bg-amber-50',
  Billing: 'text-orange-600 bg-orange-50',
  Students: 'text-pink-600 bg-pink-50',
};

interface ReportCardProps {
  report: Report;
  isLoading: boolean;
  onDownload: () => void;
}

export const ReportCard = ({
  report,
  isLoading,
  onDownload,
}: ReportCardProps) => {
  const isViewReport = report.hasViewPage && report.viewRoute;

  return (
    <div
      className={cn(
        'group rounded-xl border border-border bg-card p-4 sm:p-5',
        'shadow-sm hover:border-border/80 hover:shadow-md',
        'transition-all duration-200',
      )}
    >
      <div className='flex flex-col gap-4 md:flex-row md:items-center'>
        {/* Icon */}
        <div
          className={cn(
            'w-fit shrink-0 rounded-lg p-2.5',
            iconColors[report.category],
          )}
        >
          <report.icon className='size-5' />
        </div>

        {/* Content */}
        <div className='min-w-0 flex-1'>
          <h3 className='font-semibold leading-tight text-foreground'>
            {report.name}
          </h3>
          {report.description && (
            <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
              {report.description}
            </p>
          )}
        </div>

        {/* Action: View link or Download button */}
        {isViewReport ? (
          <Button
            variant='secondary'
            size='sm'
            className='h-9 w-full px-4 md:w-auto'
            asChild
          >
            <Link
              to={report.viewRoute!}
              className='inline-flex items-center gap-2'
            >
              <ExternalLink className='size-4' />
              View
            </Link>
          </Button>
        ) : (
          <Button
            variant='secondary'
            onClick={onDownload}
            disabled={isLoading}
            size='sm'
            className='h-9 w-full px-4 md:w-auto'
          >
            {isLoading ? <Loader2 className='animate-spin' /> : <Download />}
            Download
          </Button>
        )}
      </div>
    </div>
  );
};
